import React, { useCallback, useMemo } from 'react';
import { FlatList, RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { FeedEntry, groupFeedSections } from './feedItems';
import { FeedItem } from './FeedItem';

type FeedRow =
  | { kind: 'header'; key: string; title: string }
  | { kind: 'entry'; key: string; entry: FeedEntry };

interface FeedListProps {
  entries: FeedEntry[];
  onOpenItem: (entry: FeedEntry) => void;
  /** Alert rows only: 复盘 thesis shortcut (T7). */
  onReviewThesis?: (entry: FeedEntry) => void;
  /** Every row: 💬 讨论 new-session shortcut (T20). */
  onDiscuss?: (entry: FeedEntry) => void;
  refreshing: boolean;
  onRefresh: () => void;
}

/** Feed list: one merged stream with Today / Earlier header rows (spec §5). */
export function FeedList({
  entries,
  onOpenItem,
  onReviewThesis,
  onDiscuss,
  refreshing,
  onRefresh,
}: FeedListProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const rows = useMemo<FeedRow[]>(
    () =>
      groupFeedSections(entries, {
        today: t('feed.today', { defaultValue: 'Today' }),
        earlier: t('feed.earlier', { defaultValue: 'Earlier' }),
      }).flatMap((section) => [
        { kind: 'header' as const, key: `header-${section.key}`, title: section.title },
        ...section.data.map((entry) => ({ kind: 'entry' as const, key: entry.key, entry })),
      ]),
    [entries, t]
  );

  const renderItem = useCallback(
    ({ item }: { item: FeedRow }) =>
      item.kind === 'header' ? (
        <Text style={styles.sectionHeader}>{item.title}</Text>
      ) : (
        <FeedItem
          entry={item.entry}
          onPress={onOpenItem}
          onReviewThesis={onReviewThesis}
          onDiscuss={onDiscuss}
        />
      ),
    [styles, onOpenItem, onReviewThesis, onDiscuss]
  );

  return (
    <FlatList
      style={styles.list}
      contentContainerStyle={styles.listContent}
      data={rows}
      keyExtractor={(r) => r.key}
      renderItem={renderItem}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          tintColor={theme.colors.primary}
        />
      }
      ListEmptyComponent={
        <View style={styles.emptyWrap}>
          <Text style={styles.empty}>
            {t('feed.empty', { defaultValue: 'Nothing new right now' })}
          </Text>
        </View>
      }
    />
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    list: { flex: 1, backgroundColor: theme.colors.background },
    listContent: { paddingHorizontal: theme.spacing.lg, paddingBottom: theme.spacing.xl },
    sectionHeader: {
      paddingTop: theme.spacing.lg,
      paddingBottom: theme.spacing.xs,
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.textTertiary,
      textTransform: 'uppercase',
    },
    emptyWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
    empty: { fontSize: theme.typography.fontSizeMd, color: theme.colors.textSecondary },
  });
}
